import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Pricelist from './Pricelist';
import authService from './api-authorization/AuthorizeService';

interface GenericList {
    id: number;
    name: string;
}

interface Item {
    id: number;
    name: string;
    defaultDays: number;
    typeId: number;
    overhireRequired: boolean;
}

interface PriceListItem {
    id: number;
    tierId: number;
    customerId: number;
    customerName: string;
    itemId: number;
    itemTypeId: number;
    hirePeriod: number;
    frequencyId: number;
    locationId: number;
    metreId: number;
    baseDays: number;
    basePrice: number;
    baseMetres: number;
    useBtm: boolean;
    metreRate: number;
    travelAmount: number;
    baseAdditionalHireRate: number;
    additionalHireRate: number;
    extraHourRate: number;
    overhireRequired: boolean;
    statusId: number;
    isDirty: boolean;
}

const AdminPriceListFencingCustomData = (props: RouteComponentProps<{}>) => {
    const [itemList, setItemList] = React.useState<Item[]>([]);
    const [frequencyList, setFrequencyList] = React.useState<GenericList[]>([]);
    const [locationList, setLocationList] = React.useState<GenericList[]>([]);
    const [metresList, setMetresList] = React.useState<GenericList[]>([]);
    const [priceListItems, setPriceListItems] = React.useState<PriceListItem[]>([]);
    const [priceListRateItems, setPriceListRateItems] = React.useState<PriceListItem[]>([]);
    const [customerOptions, setCustomerOptions] = React.useState<GenericList[]>([]);
    const [systemCompanyId, setSystemCompanyId] = React.useState(3);
    const [loading, setLoading] = React.useState(true);
    const [pricelistLoading, setPricelistLoading] = React.useState(true);
    const [readOnly, setReadOnly] = React.useState(false);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});


    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckUserPermission?Permission=AdminPricelistsView&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data == true) {
                getPriceListData();
            } else if (res.data == false) {
                //redirect to 403 permission denied
                props.history.push("/accessdenied")
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }


    const getPriceListData = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/PriceList/GetPricelistData?SubId=' + user.sub + '&IsEws=false&IsPumpClean=false&IsEpa=false&IsFencing=true&IsEvent=false', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setItemList(res.data.itemList);
            setMetresList(res.data.metresList);
            setCustomerOptions(res.data.customerOptions);
            setSystemCompanyId(res.data.systemCompanyId);
            setReadOnly(res.data.readOnly);
            setLoading(false);
            getPricelist();
        }).catch(error => {
            toast.error(error.message);
        });
    }

    const getPricelist = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        axios.get('api/PriceList/GetPricelistFencingCustom', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var pricelist = res.data.priceList;
            let pricelistItem = addBlankRow();
            pricelist.push(pricelistItem);
            setPriceListItems(pricelist);

            var pricelistrate = res.data.priceListRate;
            var pricelistrateItem = addBlankRow();
            pricelistrate.push(pricelistrateItem);
            setPriceListRateItems(pricelistrate);

            setPricelistLoading(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const addBlankRow = () => {
        let pricelistItem: PriceListItem = {
            id: 0,
            tierId: -1,
            customerId: -1,
            customerName: "",
            itemId: -1,
            itemTypeId: 0,
            hirePeriod: 0,
            frequencyId: -1,
            locationId: -1,
            metreId: -1,
            baseDays: 1,
            basePrice: 0,
            baseMetres: 0,
            useBtm: false,
            metreRate: 0,
            travelAmount: 0,
            baseAdditionalHireRate: 0,
            additionalHireRate: 0,
            extraHourRate: 0,
            overhireRequired: true,
            statusId: 1,
            isDirty: false
        };

        return pricelistItem;
    }

    const updatePricelist = (pricelist: PriceListItem[]) => {
        setPriceListItems(pricelist);
    }
    
    const updatePricelistRate = (pricelistrate: PriceListItem[]) => {
        setPriceListRateItems(pricelistrate);
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (priceListItems.length === 1 && priceListRateItems.length === 1) {
            errorList["pricelistitems"] = "Please enter at least one item!";
            setErrors(errorList);
            return true;
        }

        var rowNumber = 0;
        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < priceListItems.length - 1; i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (priceListItems[i].customerId < 1) {
                newErrorString += "Customer, ";
                error = true;
            }

            if (priceListItems[i].itemId < 1) {
                newErrorString += "Item, ";
                error = true;
            }

            var hirePeriodMonths = priceListItems[i].hirePeriod * 1;
            if (hirePeriodMonths < 1) {
                newErrorString += "Hire Period, ";
                error = true;
            }

            if (priceListItems[i].itemTypeId === 50 && priceListItems[i].metreId < 1) {
                newErrorString += "Metres, ";
                error = true;
            }

            if (priceListItems[i].itemTypeId != 51 && (!priceListItems[i].metreRate || priceListItems[i].metreRate <= 0)) {
                newErrorString += "Metre Rate, ";
                error = true;
            }

            if (priceListItems[i].overhireRequired && (!priceListItems[i].additionalHireRate || priceListItems[i].additionalHireRate <= 0)) {
                newErrorString += "Additional Hire Rate, ";
                error = true;
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }

        }
        if (error) {
            formIsValid = false;
            errorList["pricelistitems"] = errorString;
        }

        var rowNo = 0;
        var errorStringRate = "Please enter the following values: ";
        var errorRate = false;

        for (var i = 0; i < priceListRateItems.length - 1; i++) {
            rowNo += 1;
            var newErrorString = "";

            if (priceListRateItems[i].itemId < 1) {
                newErrorString += "Item, ";
                errorRate = true;
            }

            if (!priceListRateItems[i].basePrice || priceListRateItems[i].basePrice <= 0) {
                newErrorString += "Price, ";
                errorRate = true;
            }

            if (priceListRateItems[i].overhireRequired && (!priceListRateItems[i].additionalHireRate || priceListRateItems[i].additionalHireRate <= 0)) {
                newErrorString += "Additional Hire Rate, ";
                errorRate = true;
            }

            if (newErrorString != "") {
                errorStringRate += "\n"
                errorStringRate += "Row " + rowNo + ": " + newErrorString;
                errorStringRate = errorStringRate.substring(0, errorStringRate.length - 2);
            }

        }
        if (errorRate) {
            formIsValid = false;
            errorList["pricelistitemsrate"] = errorStringRate;
        }

        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            var changed = priceListItems.filter(p => p.isDirty);
            var changedRate = priceListRateItems.filter(p => p.isDirty);

            if (changed.length === 0 && changedRate.length === 0) {
                toast.info("No items have changed.");
            } else {
                savePricelist(changed, changedRate);
            }
        } else {
            toast.error("Please fix validation errors before Saving")
        }
    }

    const savePricelist = async (changed: PriceListItem[], changedRate: PriceListItem[]) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var savePriceList = {
            priceListItems: changed,
            priceListItemRates: changedRate,
            subId: user.sub
        };

        axios.post('api/PriceList/SavePricelistFencing', savePriceList, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Price List Saved Successfully");
                setPricelistLoading(true);

                getPricelist();
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const renderDetails = (
        <div>
            <div>Base Days = Minimum Hire Period in Months </div>
            <div>Formula: less than 100lm: [Base Price] + ([Qty - 30] * [Metre Rate])</div>
            <div>Formula: 100lm +: [Qty] * [Metre Rate])</div>
            <div>Example: Base Price = $451 for 30lm, Metre Rate = $11 (less than 100 lm) or $9.90 (100lm to 149lm) or $8.80 (greater than 150lm)</div>
            <div>Example: 30 (lm) = $451</div>
            <div>Example: 50 (lm) = $451 (30lm) + (20lm x 11) = $220. Price = $451 + $220 = $671</div>
            <div>Example: 120 (lm) * $9.90 = $1,188</div>
            <div>Example: 150 (lm) * $8.80 = $1,408</div>
            <div className={!pricelistLoading ? "" : "hidden"}>
                {<Pricelist systemCompanyId={systemCompanyId} id={-1} priceListTierTypeId={2} pricelist={priceListItems} itemList={itemList.filter(i => i.typeId === 50)} customerOptions={customerOptions} frequencyList={frequencyList} locationList={locationList} metresList={metresList} isEpa={false} isPumpClean={false} isFencing={true} isFencingCustom={true} isEvent={false} isPriceOnly={false} update={updatePricelist} />}
                {errors["pricelistitems"] ?
                    (errors["pricelistitems"]).split("\n").map((item: any, key: any) => {
                        return <span className="label errors" key={key}>{item}<br /></span>
                    })
                    : ""}

                {<Pricelist systemCompanyId={systemCompanyId} id={-1} priceListTierTypeId={2} pricelist={priceListRateItems} itemList={itemList.filter(i => i.typeId === 51 || i.typeId === 53)} customerOptions={customerOptions} frequencyList={frequencyList} locationList={locationList} metresList={metresList} isEpa={false} isPumpClean={false} isFencing={false} isFencingCustom={true} isEvent={false} isPriceOnly={true} update={updatePricelistRate} />}
                {errors["pricelistitemsrate"] ?
                    (errors["pricelistitemsrate"]).split("\n").map((item: any, key: any) => {
                        return <span className="label errors" key={key}>{item}<br /></span>
                    })
                    : ""}
                <div className="marginBottom10">
                    <button className={readOnly ? "hidden" : "defaultbutton defaultbutton__large"} type="button" onClick={save} disabled={pricelistLoading ? true : false}>Save</button>
                </div>
            </div>
            <div className={pricelistLoading ? "" : "hidden"}><p><em>Loading...</em></p></div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            <h1>Price List Fencing Custom</h1>
            {contents}
        </div>
    );


}

export default AdminPriceListFencingCustomData;